import { useState, useRef, useEffect } from 'react';
import { Camera, X, Check, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Tesseract from 'tesseract.js';

function CameraScanner({ isOpen, onClose, onDetect }) {
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [detectedText, setDetectedText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const startCamera = async () => {
    setError(null);
    
    console.log('🎥 Starting camera...');
    console.log('Navigator mediaDevices:', !!navigator.mediaDevices);
    console.log('getUserMedia:', !!navigator.mediaDevices?.getUserMedia);
    
    try {
      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device or browser');
      }

      console.log('🎥 Requesting camera access...');
      
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      console.log('🎥 Camera constraints:', constraints);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('🎥 Camera access granted!');
      console.log('Stream tracks:', mediaStream.getTracks().length);
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        console.log('🎥 Video stream set');
      }
    } catch (err) {
      console.error('❌ Camera error:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      
      let errorMessage = 'Unable to access camera. ';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access when prompted by your browser.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += 'Camera is already in use by another app. Please close other camera apps.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage += 'Your device camera does not meet requirements. Trying again with basic settings...';
        // Try again with simpler constraints
        setTimeout(() => startCameraBasic(), 1000);
        return;
      } else {
        errorMessage += err.message || 'Please check camera permissions and try again.';
      }
      
      setError(errorMessage);
    }
  };
  
  const startCameraBasic = async () => {
    try {
      console.log('🎥 Trying basic camera constraints...');
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true // Basic constraints
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        console.log('🎥 Basic camera started successfully');
      }
    } catch (err) {
      console.error('❌ Basic camera also failed:', err);
      setError('Unable to start camera even with basic settings. ' + err.message);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas size to video size
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data
      const imageData = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageData);

      // Stop camera
      stopCamera();

      // Process image with OCR
      processImage(imageData);
    }
  };

  const processImage = async (imageData) => {
    setProcessing(true);
    
    try {
      console.log('🔍 Starting OCR processing...');
      const result = await Tesseract.recognize(imageData, 'eng', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            console.log(`Processing: ${Math.round(m.progress * 100)}%`);
          }
        }
      });

      console.log('📝 Raw OCR text:', result.data.text);
      
      // Extract and clean text
      let rawText = result.data.text.trim().toUpperCase();
      
      // Try to find UK number plate patterns in the text
      const ukPlatePattern = findUKPlatePattern(rawText);
      
      if (ukPlatePattern) {
        console.log('✅ Found UK plate pattern:', ukPlatePattern);
        setDetectedText(ukPlatePattern);
        setShowConfirmation(true);
      } else {
        // Fallback: clean all text and take first 7 chars
        let cleanText = rawText.replace(/[^A-Z0-9]/g, '');
        console.log('⚠️ No pattern match, using cleaned text:', cleanText);
        
        // Limit to reasonable plate length
        if (cleanText.length > 8) {
          cleanText = cleanText.substring(0, 7);
        }
        
        setDetectedText(cleanText);
        setShowConfirmation(true);
      }
    } catch (error) {
      console.error('OCR Error:', error);
      setError('Unable to read number plate. Please try retaking the photo or enter manually.');
      setShowConfirmation(true);
      setDetectedText('');
    } finally {
      setProcessing(false);
    }
  };
  
  const findUKPlatePattern = (text) => {
    // Remove all spaces and special characters
    const cleaned = text.replace(/[^A-Z0-9]/g, '');
    
    console.log('🔍 Looking for UK plate in:', cleaned);
    
    // UK plate patterns to try (in order of preference)
    const patterns = [
      // Current format: AB12CDE (2 letters, 2 numbers, 3 letters)
      /([A-Z]{2}\d{2}[A-Z]{3})/,
      
      // Older format: ABC123D (3 letters, 3 numbers, 1 letter)
      /([A-Z]{3}\d{3}[A-Z])/,
      
      // Older format: A123BCD (1 letter, 3 numbers, 3 letters)
      /([A-Z]\d{3}[A-Z]{3})/,
      
      // Older format: AB12CD (2 letters, 2 numbers, 2 letters)
      /([A-Z]{2}\d{2}[A-Z]{2})/,
      
      // Any valid-looking sequence: 5-7 alphanumeric with letters at start/end
      /([A-Z]{1,2}\d{2,3}[A-Z]{1,3})/,
    ];
    
    // Try each pattern
    for (const pattern of patterns) {
      const match = cleaned.match(pattern);
      if (match && match[1]) {
        const plate = match[1];
        // UK plates are 5-7 characters typically
        if (plate.length >= 5 && plate.length <= 7) {
          console.log('✅ Pattern matched:', plate);
          return plate;
        }
      }
    }
    
    // If no pattern matches, try to extract any 5-7 char sequence
    if (cleaned.length >= 5 && cleaned.length <= 7) {
      return cleaned;
    }
    
    // Take first 5-7 characters as best guess
    if (cleaned.length > 7) {
      // Try to find a 7-char window that looks like a plate
      for (let i = 0; i <= cleaned.length - 5; i++) {
        const segment = cleaned.substring(i, i + 7);
        // Check if it starts with letter and ends with letter
        if (/^[A-Z].*[A-Z]$/.test(segment)) {
          console.log('📍 Best guess segment:', segment);
          return segment;
        }
      }
      return cleaned.substring(0, 7);
    }
    
    return null;
  };

  const handleConfirm = () => {
    if (detectedText) {
      onDetect(detectedText);
      handleClose();
    }
  };

  const handleRetry = () => {
    setCapturedImage(null);
    setDetectedText('');
    setShowConfirmation(false);
    startCamera();
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setDetectedText('');
    setShowConfirmation(false);
    setError(null);
    onClose();
  };

  const handleEditText = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    // Limit to 7 characters for UK plates
    setDetectedText(value.substring(0, 7));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="w-full h-full max-w-2xl relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg"
        >
          <X className="h-6 w-6 text-gray-900" />
        </button>

        {/* Camera View or Error */}
        {!capturedImage && !showConfirmation && !error && (
          <div className="w-full h-full flex flex-col">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            
            {/* Overlay Guide */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="border-4 border-yellow-400 rounded-lg w-80 h-32 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white bg-black bg-opacity-50 px-4 py-2 rounded text-sm">
                    Position number plate here
                  </p>
                </div>
              </div>
            </div>

            {/* Capture Button */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
              <button
                onClick={capturePhoto}
                className="bg-yellow-400 hover:bg-yellow-500 rounded-full p-6 shadow-xl"
              >
                <Camera className="h-8 w-8 text-gray-900" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !showConfirmation && (
          <div className="w-full h-full flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-white p-6">
              <div className="text-center">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-4">Camera Access Error</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <div className="space-y-3">
                  <Button
                    onClick={startCamera}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Processing */}
        {processing && (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <Loader2 className="h-16 w-16 text-yellow-400 animate-spin mb-4" />
            <p className="text-white text-xl font-semibold">Reading number plate...</p>
          </div>
        )}

        {/* Confirmation */}
        {showConfirmation && !processing && (
          <div className="w-full h-full flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-white p-6">
              <h3 className="text-xl font-bold mb-4 text-center">
                Confirm Number Plate
              </h3>
              
              {/* Captured Image Preview */}
              {capturedImage && (
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full rounded-lg mb-4"
                />
              )}

              {/* Editable Number Plate */}
              <div className="uk-plate-container mb-4" style={{ justifyContent: 'center' }}>
                <div className="uk-plate">
                  <input
                    type="text"
                    value={detectedText}
                    onChange={handleEditText}
                    className="uk-plate-input"
                    maxLength={7}
                    autoFocus
                    placeholder="AB12CDE"
                  />
                </div>
              </div>

              <div className="mb-6 text-center">
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  {detectedText.length}/7 characters
                </p>
                <p className="text-xs text-gray-500">
                  Edit if needed, then confirm to search
                </p>
                {detectedText.length < 5 && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ UK plates are typically 5-7 characters
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="flex-1"
                >
                  Retake
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
                  disabled={!detectedText || detectedText.length < 5}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Confirm & Search
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Hidden canvas for image capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}

export default CameraScanner;
